"use client";

import { motion } from "framer-motion";
import { useFormatter, useTranslations } from "next-intl";

import { EYES } from "@/lib/useIcons";

import { useReveal } from "./reveal";

// Centered post header that sits at the top of the rounded black panel: tag
// pills → title → date + view count. Title is the page h1. Tags / views render
// defensively — the blog API doesn't
// always return them. `date` is the raw ISO string; it's formatted here so the
// month name follows the active locale.
export default function BlogPostHeader({ title, tags = [], date, views }) {
  const t = useTranslations("blog.post");
  const format = useFormatter();
  const { reveal } = useReveal({ amount: 0.5 });
  const safeTags = Array.isArray(tags) ? tags.slice(0, 4) : [];
  // calendar: "gregory" keeps Thai on CE years — th-TH would otherwise render
  // the Buddhist era (2569), which this site doesn't use anywhere else.
  const postedOn = date
    ? format.dateTime(new Date(date), {
        day: "numeric",
        month: "long",
        year: "numeric",
        calendar: "gregory",
      })
    : null;

  return (
    <div className="mx-auto flex max-w-[940px] flex-col items-center gap-5 px-6 pt-12 text-center md:pt-16">
      {safeTags.length > 0 && (
        <motion.div
          {...reveal}
          custom={0}
          className="flex flex-wrap items-center justify-center gap-3"
        >
          {safeTags.map((tag, i) => (
            <span
              key={`${tag}-${i}`}
              className="rounded-2xl bg-blue-500 px-3 py-2 text-sm font-normal text-stone-50"
            >
              {tag}
            </span>
          ))}
        </motion.div>
      )}

      <motion.h1
        {...reveal}
        custom={1}
        className="text-2xl font-semibold leading-snug text-white md:text-3xl"
      >
        {title}
      </motion.h1>

      <motion.div
        {...reveal}
        custom={2}
        className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-stone-400"
      >
        {postedOn && <span>{postedOn}</span>}
        {views && (
          <span className="inline-flex items-center gap-2">
            {EYES}
            {t("views", { views })}
          </span>
        )}
      </motion.div>
    </div>
  );
}
